import { createContext, useContext, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X } from "lucide-react";
import { CERTIFICATES } from "@/data/site";

const CertificateContext = createContext(null);

export const useCertificate = () => useContext(CertificateContext);

export const CertificateProvider = ({ children }) => {
  const [index, setIndex] = useState(null);
  const open = (i) => setIndex(i);
  const close = () => setIndex(null);
  const active = index != null ? CERTIFICATES[index] : null;

  return (
    <CertificateContext.Provider value={{ open, close }}>
      {children}
      <Dialog open={!!active} onOpenChange={(o) => !o && close()}>
        <DialogContent
          data-testid="cert-lightbox"
          className="max-w-3xl p-0 overflow-hidden bg-white border-none [&>button]:hidden"
        >
          {active && (
            <div>
              <div className="flex items-center justify-between p-5 border-b border-black/5">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-brand-green">{active.issuer}</p>
                  <h3 className="text-lg font-medium text-brand-ink">{active.title}</h3>
                </div>
                <button
                  data-testid="cert-lightbox-close"
                  onClick={close}
                  aria-label="Close"
                  className="grid h-9 w-9 place-items-center rounded-full bg-brand-alt text-brand-ink transition-colors hover:bg-brand-mist"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="max-h-[75vh] overflow-auto bg-brand-alt p-4">
                <img src={active.image} alt={active.title} className="w-full rounded-xl" />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </CertificateContext.Provider>
  );
};

// Compact, premium trust badges. Each opens its certificate lightbox.
export const TRUST_BADGES = [
  { label: "FDA Registered", index: 0 },
  { label: "Registered Business", index: 1 },
  { label: "Environmental Health Certified", index: 2 },
  { label: "CSIR Trained", index: 3 },
];

export const TrustBadges = ({ variant = "light", className = "" }) => {
  const ctx = useCertificate();
  const isDark = variant === "dark";
  return (
    <div className={`flex flex-wrap gap-2.5 ${className}`}>
      {TRUST_BADGES.map((b) => (
        <button
          key={b.label}
          type="button"
          data-testid={`trust-badge-${b.index}`}
          onClick={() => ctx?.open(b.index)}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all duration-300 hover:-translate-y-0.5 ${
            isDark
              ? "border-white/20 text-white/80 hover:bg-white/10 hover:text-white"
              : "border-black/10 text-brand-slate hover:border-brand-green hover:text-brand-green bg-white/60"
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${isDark ? "bg-brand-mist" : "bg-brand-green"}`} />
          {b.label}
        </button>
      ))}
    </div>
  );
};
